import { FC, useCallback, useEffect } from "react";
import { trackEvent } from "~/utils/analytics";
import { getTourSteps } from "../constants/tourConfig";
import { TourId, useTourStore } from "../store/useTourStore";
import { TourOverlay } from "./TourOverlay";
import { TourTooltip } from "./TourTooltip";

interface TourProps {
  tourId: TourId;
  enabled?: boolean;
  onComplete?: () => void;
}

const ELEMENT_WAIT_TIMEOUT = 3000;
const ELEMENT_POLL_INTERVAL = 500;

const waitForElement = (
  selector: string,
  timeout = ELEMENT_WAIT_TIMEOUT
): Promise<Element | null> => {
  return new Promise((resolve) => {
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(interval);
        resolve(el);
      } else if (Date.now() - startTime > timeout) {
        clearInterval(interval);
        resolve(null);
      }
    }, ELEMENT_POLL_INTERVAL);
  });
};

export const Tour: FC<TourProps> = ({ tourId, enabled = true, onComplete }) => {
  const {
    activeTour,
    currentStepIndex,
    startTour,
    nextStep,
    skipTour,
    completeTour,
    isTourCompleted,
  } = useTourStore();

  const steps = getTourSteps(tourId);
  const currentStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;
  const isActive = activeTour === tourId;

  const handleStartTour = useCallback(async () => {
    if (!enabled || isTourCompleted(tourId)) {
      return;
    }

    const firstStep = steps[0];
    if (!firstStep) return;

    const element = await waitForElement(firstStep.target);
    if (!element) return;

    startTour(tourId);
    trackEvent({
      eventName: "OnboardingTour_Started",
      properties: { tourId, step: firstStep.id },
    });
  }, [enabled, tourId, steps, startTour, isTourCompleted]);

  const handleSkip = useCallback(() => {
    if (currentStep) {
      trackEvent({
        eventName: "OnboardingTour_Skipped",
        properties: { tourId, step: currentStep.id },
      });
    }
    skipTour();
  }, [tourId, currentStep, skipTour]);

  const handleNext = useCallback(() => {
    if (currentStep) {
      trackEvent({
        eventName: "OnboardingTour_StepCompleted",
        properties: { tourId, step: currentStep.id },
      });
    }
    nextStep();
  }, [tourId, currentStep, nextStep]);

  const handleComplete = useCallback(() => {
    trackEvent({
      eventName: "OnboardingTour_Completed",
      properties: { tourId, totalSteps: steps.length },
    });
    completeTour();
    onComplete?.();
  }, [tourId, steps.length, completeTour, onComplete]);

  useEffect(() => {
    if (!isActive || !currentStep?.advanceOnTargetClick) return;

    const element = document.querySelector(currentStep.target);
    if (!element) return;

    const handleClick = () => {
      if (isLastStep) {
        handleComplete();
      } else {
        handleNext();
      }
    };

    element.addEventListener("click", handleClick);
    return () => {
      element.removeEventListener("click", handleClick);
    };
  }, [isActive, currentStep, isLastStep, handleNext, handleComplete]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleStartTour();
    }, 500);
    return () => clearTimeout(timer);
  }, [handleStartTour]);

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <TourOverlay
      targetSelector={currentStep.target}
      isVisible={isActive}
      allowTargetInteraction={currentStep.advanceOnTargetClick}
    >
      <TourTooltip
        targetSelector={currentStep.target}
        titleKey={currentStep.titleKey}
        textKey={currentStep.textKey}
        position={currentStep.position}
        tooltipAlign={currentStep.tooltipAlign}
        arrowOffset={currentStep.arrowOffset}
        isVisible={isActive}
        showSkip={!currentStep.advanceOnTargetClick}
        showNext={!isLastStep && !currentStep.advanceOnTargetClick}
        showDone={isLastStep && !currentStep.advanceOnTargetClick}
        onSkip={handleSkip}
        onNext={handleNext}
        onDone={handleComplete}
      />
    </TourOverlay>
  );
};
